// Phase 2.8 — REST-backed notificationService.
// Phase 4 wired in: subscribeForUser uses Socket.io for live deltas with
// a polling fallback only when the socket fails to connect.

import { getAccessToken } from '../contexts/AuthContextV2';
import { joinRoom, leaveRoom, on as socketOn, isConnected } from './socketClient';

const API_BASE = process.env.REACT_APP_API_URL ?? '';

async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  let body = null;
  try { body = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export const notificationService = {
  async createNotification({ recipientUid, channel, type, title, body, payload, studentId }) {
    if (!recipientUid) throw new Error('createNotification: recipientUid is required');
    const { notification } = await apiFetch('/api/notifications', {
      method: 'POST',
      body: JSON.stringify({ recipientUid, channel, type, title, body, payload, studentId })
    });
    return notification.id;
  },

  async listForUser(uid, { max = 30, onlyUnread = false } = {}) {
    // uid is implied by JWT; the param is kept for API compatibility.
    if (!uid) return [];
    try {
      const q = `?limit=${max}${onlyUnread ? '&read=false' : ''}`;
      const { notifications } = await apiFetch(`/api/notifications${q}`);
      return notifications;
    } catch (err) {
      console.warn('notificationServiceV2.listForUser failed:', err.message);
      return [];
    }
  },

  // Phase 4 — Socket.io subscription with REST fallback.
  // tenantId is only needed when the caller knows which tenant the
  // recipient belongs to; if omitted the per-user room is implicit
  // (the backend auto-joins it on connect).
  subscribeForUser(uid, callback, { max = 20, tenantId = null, fallbackPollMs = 30000 } = {}) {
    if (!uid || typeof callback !== 'function') return () => {};
    let cancelled = false;
    let items = [];
    const refresh = async () => {
      if (cancelled) return;
      try {
        items = await notificationService.listForUser(uid, { max });
        if (!cancelled) callback(items, null);
      } catch (err) {
        if (!cancelled) callback([], err);
      }
    };
    const handleCreated = (n) => {
      if (cancelled || !n) return;
      items = [n, ...items.filter((x) => x.id !== n.id)].slice(0, max);
      callback(items, null);
    };
    const handleUpdated = (n) => {
      if (cancelled || !n) return;
      items = items.map((x) => (x.id === n.id ? n : x));
      callback(items, null);
    };
    const offs = [];
    refresh();
    offs.push(socketOn('notification:created', handleCreated));
    offs.push(socketOn('notification:updated', handleUpdated));
    if (tenantId) {
      joinRoom(`tenant:${tenantId}:notifications:user:${uid}`);
    }
    // Polling fallback only fires if the socket isn't connected after
    // a generous grace window — the user is offline / proxy stripped WS.
    const pollId = setInterval(() => { if (!isConnected()) refresh(); }, fallbackPollMs);
    return () => {
      cancelled = true;
      offs.forEach((off) => { try { off(); } catch { /* ignore */ } });
      clearInterval(pollId);
      if (tenantId) leaveRoom(`tenant:${tenantId}:notifications:user:${uid}`);
    };
  },

  async markAsRead(notificationId) {
    if (!notificationId) return;
    try {
      await apiFetch(`/api/notifications/${encodeURIComponent(notificationId)}/read`, { method: 'PATCH' });
    } catch (err) {
      console.warn('notificationServiceV2.markAsRead failed:', err.message);
    }
  },

  async markAllAsRead(uid) {
    // Server doesn't expose a bulk endpoint yet; emulate client-side.
    const items = await notificationService.listForUser(uid, { onlyUnread: true, max: 200 });
    let n = 0;
    for (const it of items) {
      try { await notificationService.markAsRead(it.id); n += 1; } catch { /* ignore */ }
    }
    return n;
  }
};

export default notificationService;
